import React, { useEffect, useState } from 'react';
import { req } from '../services/api';
import Modal from '../components/Modal';

const money = value => Number(value || 0).toFixed(2) + ' €';

const formatDate = value => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('cs-CZ');
};

const normalize = value =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const DOCS = [
  ['idCardFront', 'Občanský průkaz – přední strana'],
  ['idCardBack', 'Občanský průkaz – zadní strana'],
  ['drivingLicenseFront', 'Řidičský průkaz – přední strana'],
  ['drivingLicenseBack', 'Řidičský průkaz – zadní strana'],
  ['taxiCard', 'Průkaz řidiče taxi'],
  ['registryDoc', 'Výpis / registrace'],
  ['bankStatement', 'Doklad k bankovnímu účtu'],
  ['signedGdpr', 'Podepsané GDPR'],
  ['signedLicense', 'Podepsaná licence'],
  ['signedVop', 'Podepsané obchodní podmínky']
];

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const [detailModal, setDetailModal] = useState(false);
  const [docsModal, setDocsModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);

  const [currentDriver, setCurrentDriver] = useState(null);
  const [docsDriver, setDocsDriver] = useState(null);
  const [currentDocs, setCurrentDocs] = useState(null);

  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    ico: ''
  });

  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    setLoading(true);

    try {
      const data = await req('GET', '/drivers?type=driver');
      if (Array.isArray(data)) setDrivers(data);
    } catch (error) {
      console.error('Drivers load failed:', error);
      alert(
        'Chyba p\u0159i na\u010d\u00edt\u00e1n\u00ed \u0159idi\u010d\u016f: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    } finally {
      setLoading(false);
    }
  };

  const q = normalize(search);

  const filtered = drivers.filter(d => {
    const matchesSearch =
      normalize(d.user?.name).includes(q) ||
      normalize(d.user?.email).includes(q) ||
      normalize(d.user?.phone).includes(q) ||
      normalize(d.ico).includes(q) ||
      normalize(d.activeVehicle?.plate).includes(q) ||
      normalize(d.activeVehicle?.brand).includes(q);

    const matchesFilter =
      filter === 'all' ||
      (filter === 'approval' && !d.approved && !d.blocked) ||
      (filter === 'documents' && !d.docsApproved && !d.blocked) ||
      (filter === 'online' && d.isOnline) ||
      (filter === 'blocked' && d.blocked);

    return matchesSearch && matchesFilter;
  });

  const pendingAccounts = drivers.filter(
    d => !d.approved && !d.blocked
  ).length;

  const pendingDocs = drivers.filter(
    d => !d.docsApproved && !d.blocked
  ).length;

  const onlineDrivers = drivers.filter(d => d.isOnline).length;

  const totalWallet = drivers.reduce(
    (sum, d) => sum + Number(d.walletBalance || 0),
    0
  );

  const approve = async id => {
    try {
      await req('PATCH', '/drivers/' + id, { approved: true });
      await loadDrivers();
    } catch (error) {
      alert(
        'Chyba p\u0159i schvalov\u00e1n\u00ed \u0159idi\u010de: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const block = async (id, blocked) => {
    try {
      await req('PATCH', '/drivers/' + id, { blocked });
      await loadDrivers();
    } catch (error) {
      alert(
        'Chyba p\u0159i zm\u011bn\u011b stavu \u0159idi\u010de: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const deleteAccount = async d => {
    if (
      !window.confirm(
        'Opravdu chcete odstranit \u00fa\u010det \u0159idi\u010de ' +
          d.user.name +
          ' z provozu? \u00da\u010det ji\u017e nebude mo\u017en\u00e9 pou\u017e\u00edvat, ale historie j\u00edzd a financ\u00ed z\u016fstane zachov\u00e1na.'
      )
    ) {
      return;
    }

    try {
      const result = await req('DELETE', '/users/' + d.user.id);

      if (result?.success) {
        alert('\u00da\u010det byl odstran\u011bn z provozu.');
        setDetailModal(false);
        await loadDrivers();
      } else {
        alert(
          'Chyba p\u0159i odstra\u0148ov\u00e1n\u00ed: ' +
            (result?.error || 'nezn\u00e1m\u00e1 chyba')
        );
      }
    } catch (error) {
      alert(
        'Chyba p\u0159i odstra\u0148ov\u00e1n\u00ed: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const openDetail = d => {
    setCurrentDriver(d);
    setDetailModal(true);
  };

  const openDocs = async d => {
    setDocsDriver(d);
    setCurrentDocs(null);
    setDocsModal(true);

    try {
      const data = await req(
        'GET',
        '/drivers/' + d.id + '/documents'
      );

      setCurrentDocs(data);
    } catch (error) {
      setDocsModal(false);
      alert(
        'Chyba p\u0159i na\u010d\u00edt\u00e1n\u00ed dokument\u016f: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const approveDocs = async approved => {
    try {
      await req(
        'PATCH',
        '/drivers/' + docsDriver.id + '/documents/approve',
        { approved }
      );

      setDocsModal(false);
      await loadDrivers();

      alert(
        approved
          ? '\u2713 Dokumenty schv\u00e1leny'
          : 'Dokumenty zam\u00edtnuty'
      );
    } catch (error) {
      alert(
        'Chyba p\u0159i zpracov\u00e1n\u00ed dokument\u016f: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const openDocument = value => {
    if (!value) return;

    let documentValue = value;

    if (typeof documentValue === 'string') {
      const trimmed = documentValue.trim();

      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            documentValue = parsed.find(Boolean) || null;
          }
        } catch (e) {
          console.error('Nepodarilo se nacist JSON dokument:', e);
        }
      }
    }

    if (Array.isArray(documentValue)) {
      documentValue = documentValue.find(Boolean) || null;
    }

    if (!documentValue || typeof documentValue !== 'string') {
      alert('Dokument nelze zobrazit.');
      return;
    }

    const w = window.open();

    if (!w) {
      alert('Prohlížeč zablokoval otevření dokumentu.');
      return;
    }

    if (documentValue.startsWith('data:application/pdf')) {
      w.document.write(
        '<iframe src="' +
          documentValue +
          '" style="position:fixed;inset:0;width:100%;height:100%;border:0;"></iframe>'
      );
    } else {
      w.document.write(
        '<body style="margin:0;background:#111;text-align:center;">' +
          '<img src="' +
          documentValue +
          '" style="max-width:100%;max-height:100vh;" />' +
          '</body>'
      );
    }

    w.document.close();
  };

  const openEdit = d => {
    setCurrentDriver(d);
    setEditForm({
      name: d.user?.name || '',
      email: d.user?.email || '',
      phone: d.user?.phone || '',
      ico: d.ico || ''
    });
    setEditModal(true);
  };

  const saveEdit = async () => {
    try {
      await req(
        'PATCH',
        '/drivers/' + currentDriver.id + '/edit',
        editForm
      );

      setEditModal(false);
      await loadDrivers();
      alert('\u2713 \u0158idi\u010d byl upraven');
    } catch (error) {
      alert(
        'Chyba p\u0159i \u00faprav\u011b \u0159idi\u010de: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const openPassword = d => {
    setCurrentDriver(d);
    setNewPassword('');
    setPasswordModal(true);
  };

  const savePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Heslo mus\u00ed m\u00edt alespo\u0148 6 znak\u016f');
      return;
    }

    try {
      await req(
        'PATCH',
        '/users/' + currentDriver.user.id + '/password',
        { password: newPassword }
      );

      setPasswordModal(false);
      alert('\u2713 Heslo bylo zm\u011bn\u011bno');
    } catch (error) {
      alert(
        'Chyba p\u0159i zm\u011bn\u011b hesla: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  return (
    <div>
      <div className="page-title">Řidiči</div>
      <div className="page-sub">
        Samostatní řidiči, jejich schválení, dokumenty, vozidla a provoz
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{drivers.length}</div>
          <div className="stat-label">Celkem řidičů</div>
        </div>

        <div className={pendingAccounts > 0 ? 'stat-card alert' : 'stat-card'}>
          <div className="stat-value">{pendingAccounts}</div>
          <div className="stat-label">Čeká schválení účtu</div>
        </div>

        <div className={pendingDocs > 0 ? 'stat-card alert' : 'stat-card'}>
          <div className="stat-value">{pendingDocs}</div>
          <div className="stat-label">Čekají dokumenty</div>
        </div>

        <div className={onlineDrivers > 0 ? 'stat-card active' : 'stat-card'}>
          <div className="stat-value">{onlineDrivers}</div>
          <div className="stat-label">Online</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{money(totalWallet)}</div>
          <div className="stat-label">V peněženkách</div>
        </div>
      </div>

      <div className="admin-toolbar">
        <input
          className="search-bar"
          placeholder="Hledat jméno, e-mail, telefon, IČO nebo SPZ..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <button
          className={'btn-sm ' + (filter === 'all' ? 'btn-gold' : 'btn-gray')}
          onClick={() => setFilter('all')}
        >
          Všichni
        </button>

        <button
          className={'btn-sm ' + (filter === 'approval' ? 'btn-red' : 'btn-gray')}
          onClick={() => setFilter('approval')}
        >
          Čeká účet
        </button>

        <button
          className={'btn-sm ' + (filter === 'documents' ? 'btn-red' : 'btn-gray')}
          onClick={() => setFilter('documents')}
        >
          Čekají dokumenty
        </button>

        <button
          className={'btn-sm ' + (filter === 'online' ? 'btn-green' : 'btn-gray')}
          onClick={() => setFilter('online')}
        >
          Online
        </button>

        <button
          className={'btn-sm ' + (filter === 'blocked' ? 'btn-red' : 'btn-gray')}
          onClick={() => setFilter('blocked')}
        >
          Blokovaní
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Samostatní řidiči</span>
          <span className="dashboard-muted">
            Zobrazeno {filtered.length} z {drivers.length}
          </span>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Řidič</th>
                <th>Účet</th>
                <th>Dokumenty</th>
                <th>Provoz</th>
                <th>Jízdy</th>
                <th>Vozidla</th>
                <th>Pracovní režim</th>
                <th>Hodnocení</th>
                <th>Peněženka</th>
                <th>Akce</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="loading-cell">
                    Načítám...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="10" className="empty-cell">
                    Žádní řidiči
                  </td>
                </tr>
              ) : (
                filtered.map(d => (
                  <tr key={d.id}>
                    <td>
                      <strong>{d.user?.name || '—'}</strong>
                      <br />
                      <span className="table-secondary">
                        {d.user?.email || '—'}
                      </span>
                    </td>

                    <td>
                      {d.blocked ? (
                        <span className="badge badge-red">Blokován</span>
                      ) : d.approved ? (
                        <span className="badge badge-green">Schválen</span>
                      ) : (
                        <span className="badge badge-gold">Čeká</span>
                      )}
                    </td>

                    <td>
                      {d.docsApproved ? (
                        <span className="badge badge-green">Schváleny</span>
                      ) : (
                        <span className="badge badge-gold">Čekají</span>
                      )}
                    </td>

                    <td>
                      {d.isOnline ? (
                        <span className="badge badge-green">Online</span>
                      ) : (
                        <span className="badge badge-gray">Offline</span>
                      )}
                    </td>

                    <td>
                      <strong>{d.totalRides || 0}</strong>
                    </td>

                    <td>
                      <strong>{d._count?.vehicles || 0}</strong>
                      {d.activeVehicle && (
                        <>
                          <br />
                          <span className="table-secondary">
                            {d.activeVehicle.brand} · {d.activeVehicle.plate}
                          </span>
                        </>
                      )}
                    </td>

                    <td>
                      {d.workMode ? (
                        <>
                          <strong>{d.workMode}</strong>
                          {d.workRadius && (
                            <>
                              <br />
                              <span className="table-secondary">
                                {d.workRadius} km
                              </span>
                            </>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>

                    <td>
                      <span className="gold-text">
                        ★ {Number(d.rating || 0).toFixed(1)}
                      </span>
                    </td>

                    <td className="gold-text">
                      {money(d.walletBalance)}
                    </td>

                    <td>
                      <div className="actions">
                        <button
                          className="btn-sm btn-blue"
                          onClick={() => openDetail(d)}
                        >
                          Detail
                        </button>

                        <button
                          className={
                            'btn-sm ' +
                            (d.docsApproved ? 'btn-green' : 'btn-gold')
                          }
                          onClick={() => openDocs(d)}
                        >
                          Dokumenty
                        </button>

                        {!d.approved && !d.blocked && (
                          <button
                            className="btn-sm btn-green"
                            onClick={() => approve(d.id)}
                          >
                            Schválit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detailModal && currentDriver && (
        <Modal
          title="Detail řidiče"
          onClose={() => setDetailModal(false)}
        >
          <div className="payout-detail-box">
            <div className="payout-row">
              <span className="payout-row-label">Jméno</span>
              <span className="payout-row-value">
                {currentDriver.user?.name || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">E-mail</span>
              <span className="payout-row-value">
                {currentDriver.user?.email || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Telefon</span>
              <span className="payout-row-value">
                {currentDriver.user?.phone || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Adresa</span>
              <span className="payout-row-value">
                {currentDriver.user?.address || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Jazyk</span>
              <span className="payout-row-value">
                {(currentDriver.user?.language || 'cs').toUpperCase()}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">IČO</span>
              <span className="payout-row-value">
                {currentDriver.ico || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Číslo licence</span>
              <span className="payout-row-value">
                {currentDriver.licenseNumber || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Účet</span>
              <span className="payout-row-value">
                {currentDriver.blocked
                  ? 'Blokován'
                  : currentDriver.approved
                  ? 'Schválen'
                  : 'Čeká na schválení'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Dokumenty</span>
              <span className="payout-row-value">
                {currentDriver.docsApproved
                  ? 'Schváleny'
                  : 'Čekají na schválení'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Online</span>
              <span className="payout-row-value">
                {currentDriver.isOnline ? 'Ano' : 'Ne'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Jízdy</span>
              <span className="payout-row-value">
                {currentDriver.totalRides || 0}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Hodnocení</span>
              <span className="payout-row-value">
                {Number(currentDriver.rating || 0).toFixed(1)}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Vozidla</span>
              <span className="payout-row-value">
                {currentDriver._count?.vehicles || 0}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Aktivní vozidlo</span>
              <span className="payout-row-value">
                {currentDriver.activeVehicle
                  ? currentDriver.activeVehicle.brand +
                    ' · ' +
                    currentDriver.activeVehicle.plate
                  : '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Pracovní režim</span>
              <span className="payout-row-value">
                {currentDriver.workMode || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Pracovní rádius</span>
              <span className="payout-row-value">
                {currentDriver.workRadius
                  ? currentDriver.workRadius + ' km'
                  : '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Peněženka</span>
              <span className="payout-row-value gold-text">
                {money(currentDriver.walletBalance)}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">Registrace</span>
              <span className="payout-row-value">
                {formatDate(currentDriver.createdAt)}
              </span>
            </div>
          </div>

          <div className="actions">
            <button
              className="btn-sm btn-gold"
              onClick={() => {
                setDetailModal(false);
                openDocs(currentDriver);
              }}
            >
              Dokumenty
            </button>

            <button
              className="btn-sm btn-gold"
              onClick={() => {
                setDetailModal(false);
                openEdit(currentDriver);
              }}
            >
              Upravit
            </button>

            <button
              className="btn-sm btn-gold"
              onClick={() => {
                setDetailModal(false);
                openPassword(currentDriver);
              }}
            >
              Změnit heslo
            </button>

            {!currentDriver.blocked ? (
              <button
                className="btn-sm btn-red"
                onClick={async () => {
                  await block(currentDriver.id, true);
                  setDetailModal(false);
                }}
              >
                Blokovat
              </button>
            ) : (
              <button
                className="btn-sm btn-green"
                onClick={async () => {
                  await block(currentDriver.id, false);
                  setDetailModal(false);
                }}
              >
                Odblokovat
              </button>
            )}

            <button
              className="btn-sm btn-red"
              onClick={() => deleteAccount(currentDriver)}
            >
              Smazat účet
            </button>
          </div>
        </Modal>
      )}

      {docsModal && docsDriver && (
        <Modal
          title={'Dokumenty – ' + (docsDriver.user?.name || 'řidič')}
          onClose={() => setDocsModal(false)}
          actions={
            currentDocs ? (
              <>
                <button
                  className="btn-gray"
                  onClick={() => approveDocs(false)}
                >
                  Zamítnout
                </button>
                <button
                  className="btn"
                  onClick={() => approveDocs(true)}
                >
                  Schválit dokumenty
                </button>
              </>
            ) : null
          }
        >
          {!currentDocs ? (
            <div className="loading-cell">Načítám dokumenty...</div>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                {currentDocs.docsApproved ? (
                  <span className="badge badge-green">
                    Dokumenty jsou schválené
                  </span>
                ) : (
                  <span className="badge badge-gold">
                    Dokumenty čekají na schválení
                  </span>
                )}
              </div>

              <div className="document-list">
                {DOCS.map(([key, label]) => (
                  <div className="document-row" key={key}>
                    <div>
                      <strong>{label}</strong>
                      <div className="table-secondary">
                        {currentDocs[key]
                          ? 'Dokument nahrán'
                          : 'Nenahráno'}
                      </div>
                    </div>

                    {currentDocs[key] && (
                      <button
                        className="btn-sm btn-blue"
                        onClick={() => openDocument(currentDocs[key])}
                      >
                        Zobrazit
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </Modal>
      )}

      {editModal && currentDriver && (
        <Modal
          title="Editace řidiče"
          onClose={() => setEditModal(false)}
          actions={
            <>
              <button
                className="btn-gray"
                onClick={() => setEditModal(false)}
              >
                Zrušit
              </button>
              <button className="btn" onClick={saveEdit}>
                Uložit
              </button>
            </>
          }
        >
          <div className="edit-row">
            <label className="edit-label">Jméno</label>
            <input
              value={editForm.name}
              onChange={e =>
                setEditForm({ ...editForm, name: e.target.value })
              }
            />
          </div>

          <div className="edit-row">
            <label className="edit-label">E-mail</label>
            <input
              value={editForm.email}
              onChange={e =>
                setEditForm({ ...editForm, email: e.target.value })
              }
            />
          </div>

          <div className="edit-row">
            <label className="edit-label">Telefon</label>
            <input
              value={editForm.phone}
              onChange={e =>
                setEditForm({ ...editForm, phone: e.target.value })
              }
            />
          </div>

          <div className="edit-row">
            <label className="edit-label">IČO</label>
            <input
              value={editForm.ico}
              onChange={e =>
                setEditForm({ ...editForm, ico: e.target.value })
              }
            />
          </div>
        </Modal>
      )}

      {passwordModal && currentDriver && (
        <Modal
          title="Změna hesla"
          onClose={() => setPasswordModal(false)}
          actions={
            <>
              <button
                className="btn-gray"
                onClick={() => setPasswordModal(false)}
              >
                Zrušit
              </button>
              <button className="btn" onClick={savePassword}>
                Uložit
              </button>
            </>
          }
        >
          <p
            style={{
              color: 'var(--gray)',
              fontSize: 13,
              marginBottom: 16
            }}
          >
            Řidič: {currentDriver.user?.name}
          </p>

          <input
            type="password"
            placeholder="Nové heslo (min. 6 znaků)"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
          />
        </Modal>
      )}
    </div>
  );
}
